import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useCompanyAccountParams } from 'src/hooks/routerCompat';
import { ensureUserSettings } from '../../actions/userSettingsActions';
import config from '../../config';
import { AppState } from '../../models/state/appState';
import { UserSettings } from '../../models/state/userSettings';
import { getBaseUrl, getUserSetting } from '../../services/helpers';
import CompanyRoutes from '../company/CompanyRoutes';
import NotificationList from '../notification/NotificationList';
import NotificationManager from '../notification/NotificationManager';
import TopNav from './navigation/TopNav';

const CompanyRoutesOutlet = () => {
  const { companyId, accountId } = useCompanyAccountParams();
  if (!companyId) {
    return null;
  }
  return <CompanyRoutes companyId={companyId} accountId={accountId} />;
};

export default function Layout() {
  const dispatch: Dispatch<any> = useDispatch();
  const userId = useSelector<AppState, string | undefined>(
    (state) => state.loginDetails.user && state.loginDetails.user.user.profile.sub
  );
  const impersonatorId = useSelector<AppState, string | undefined>(
    (state) => state.loginDetails.user && state.loginDetails.user.impersonatorId
  );
  const savedUserSettings = useSelector<AppState, UserSettings>((state) => state.userSettings);
  const userSettingLoaded = useSelector<AppState, boolean>(
    (state) => state.userSettings.userId.length > 0 && state.userSettings.isLoading == false
  );

  useEffect(() => {
    if (userId) {
      dispatch(ensureUserSettings(userId));
    }
  }, [dispatch, userId]);

  // No dependency array: mirrors the original unguarded componentDidUpdate, which re-checked the
  // redirect after every render because the settings can arrive on any store update.
  useEffect(() => {
    if (window.location.pathname === '/' && savedUserSettings && savedUserSettings.companyId) {
      const { companyId, accountId, userSettings } = savedUserSettings;
      const defaultView = getUserSetting('defaultView', userSettings)?.settingValue;
      const hideDashboard = getUserSetting('hideDashboard', userSettings)?.settingValue?.toLowerCase() === 'true';
      const landingPage = !hideDashboard && defaultView === 'Dashboard' ? 'dashboard' : 'sim';
      window.location.href = `${getBaseUrl(companyId, accountId)}${landingPage}`;
    }
  });

  const userAccountSetupNotComplete =
    userSettingLoaded && savedUserSettings?.accountId === null && savedUserSettings?.companyId === null;
  const returnPath = `${window.location.pathname}${window.location.search}`;

  return (
    <div className="container-fluid layout h-100 vwrapper p-0">
      {!userSettingLoaded ? (
        <></>
      ) : (
        <>
          {impersonatorId && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center m-0 rounded-0 py-2">
              <span>You are impersonating another user</span>
              <a
                className="btn btn-light btn-sm"
                href={`${config.AUTH_BASE_URL}/Account/StopImpersonating?returnPath=${encodeURIComponent(returnPath)}`}
              >
                Return to your login
              </a>
            </div>
          )}
          <TopNav />
          <div id="lower-pane" className="row vcontent vwrapper">
            <div className="col-xs-12 h-100">
              <div className="wrapper h-100">
                <div id="content" className="h-100">
                  {userAccountSetupNotComplete ? (
                    <div className="m-4">
                      <h3>You have no permissions, please contact your account manager</h3>
                    </div>
                  ) : (
                    <>
                      <NotificationManager />
                      <NotificationList />
                      <div className="h-100">
                        <CompanyRoutesOutlet />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
